<template>
  <div class="campaign-actions ml-1">
    <router-link
      v-if="!campaign.disabled"
      :to="`/campaigns/${campaign.id}/creatives/new`"
    >
      <el-button
        :class="{ 'pulse-button': pulsating }"
        class="mr-1"
        type="primary"
        size="small"
        icon="PlusElementIcon"
        round
      >
        Add creative
      </el-button>
    </router-link>
    <el-tooltip
      class="item"
      effect="dark"
      placement="top-start"
      content="Archive"
    >
      <span>
        <el-popconfirm
          confirm-button-text="OK"
          cancel-button-text="No, Thanks"
          confirm-button-type="danger"
          icon="InfoFilledElementIcon"
          icon-color="red"
          :title="`Are you sure to archive '${campaign.name}'?`"
          @confirm="onArchive"
        >
          <template #reference>
            <el-button
              type="info"
              plain
              circle
              size="small"
              icon="DeleteElementIcon"
              class="small-action-btn"
            />
          </template>
        </el-popconfirm>
      </span>
    </el-tooltip>
    <el-tooltip class="item" effect="dark" placement="top-start" content="Edit">
      <router-link :to="`/campaigns/edit/${campaign.id}`" title="Edit">
        <el-button
          type="success"
          plain
          circle
          size="small"
          icon="EditElementIcon"
          class="small-action-btn"
        />
      </router-link>
    </el-tooltip>
    <el-tooltip
      v-if="showButton"
      class="item"
      effect="dark"
      placement="top-start"
      content="History"
    >
      <router-link :to="`/history/Campaign/${campaign.id}`">
        <el-button
          type="info"
          plain
          circle
          size="small"
          icon="TimerElementIcon"
          class="small-action-btn"
        />
      </router-link>
    </el-tooltip>
    <el-tooltip
      class="item"
      effect="dark"
      placement="top-start"
      content="View Report"
    >
      <el-button
        type="info"
        plain
        circle
        size="small"
        icon="PieChartElementIcon"
        class="small-action-btn mr-02"
        @click="viewReport(campaign.id)"
      />
    </el-tooltip>
    <el-tooltip
      class="item"
      effect="dark"
      placement="top-start"
      content="Clone campaign"
    >
      <el-dropdown trigger="click" @command="openModal">
        <el-button
          type="primary"
          plain
          circle
          size="small"
          icon="CopyDocumentElementIcon"
          class="small-action-btn"
        />
        <template #dropdown>
          <el-dropdown-menu class="clone-champaign-dropdown">
            <el-dropdown-item>
              <router-link
                :to="`/campaigns/clone/${campaign.id}`"
                title="Clone only campaign"
              >
                <el-button
                  :class="{ 'pulse-button': pulsating }"
                  class="mr-1"
                  type="primary"
                  size="small"
                  icon="PlusElementIcon"
                  round
                >
                  Clone only campaign
                </el-button>
              </router-link>
            </el-dropdown-item>
            <el-dropdown-item :command="1">
              <el-button
                :class="{ 'pulse-button': pulsating }"
                class="mr-1"
                type="primary"
                size="small"
                icon="PlusElementIcon"
                round
              >
                Clone campaign with creatives
              </el-button>
            </el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </el-tooltip>
    <el-tooltip
      v-if="campaign.flat_deal"
      class="item"
      effect="dark"
      placement="top-start"
      content="View"
    >
      <router-link :to="`/direct_deals/${campaign.id}/edit`" title="View">
        <el-button
          type="primary"
          plain
          circle
          size="small"
          icon="ViewElementIcon"
          class="small-action-btn"
        />
      </router-link>
    </el-tooltip>
    <template v-if="showButton">
      <el-tooltip
        class="item"
        effect="dark"
        placement="top-start"
        content="Delete"
      >
        <span>
          <el-popconfirm
            confirm-button-text="OK"
            cancel-button-text="No, Thanks"
            confirm-button-type="danger"
            icon="InfoFilledElementIcon"
            icon-color="red"
            :title="`Are you sure to delete '${campaign.name}'?`"
            @confirm="onDelete"
          >
            <template #reference>
              <el-button
                type="danger"
                plain
                circle
                size="small"
                icon="DeleteElementIcon"
                class="small-action-btn"
              />
            </template>
          </el-popconfirm>
        </span>
      </el-tooltip>
    </template>
    <el-dialog
      v-model="dialogFormVisible"
      :title="modalTitle"
      width="30%"
      :modal="false"
      @close="closeModal"
    >
      <div class="modal-content">
        <div
          v-for="creative in creatives"
          :key="creative.id"
          class="content-item"
        >
          <input
            :id="creative.id"
            v-model="creativesToClone"
            type="checkbox"
            :name="creative.name"
            :value="creative.id"
          />
          <label :for="creative.name">
            <span class="item-id">{{ creative.id }}.</span>
            {{ creative.name }}</label
          >
        </div>
      </div>
      <template #footer>
        <span class="dialog-footer">
          <el-button type="primary" @click="cloneWithCreatives(campaign.id)">
            Next
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref } from 'vue'
import { useCampaignsStore } from '@/apps/traforama/store/campaigns'
import { ElMessage } from 'element-plus'
import { useCreativesStore } from '@/apps/traforama/store/creatives'
import useRouter from '@/hooks/useRouter'
import { ICreativeAttributes } from '@/api/creatives'

export default defineComponent({
  name: 'CampaignActions',
  props: {
    campaign: {
      type: Object,
      required: true
    },
    showButton: {
      type: Boolean,
      default: false
    },
    pulsating: {
      type: Boolean,
      default: false
    },
    creativesIds: {
      type: Array,
      default: () => []
    }
  },
  setup(props, context) {
    const creativesStore = useCreativesStore()
    const campaignsStore = useCampaignsStore()
    const onDelete = () => {
      const campaignId = props.campaign.id.toString()
      campaignsStore.DestroyItem(campaignId).then(() => {
        ElMessage.success({
          message: 'Campaign has been deleted',
          type: 'success'
        })
      })
    }

    const onArchive = () => {
      const campaignId = props.campaign.id.toString()
      campaignsStore.ArchiveCampaign(campaignId).then(() => {
        ElMessage.success({
          message: 'Campaign has been archived',
          type: 'success'
        })
      })
    }

    const creatives = computed(() => {
      let creatives: any = []
      if (props.creativesIds) {
        props.creativesIds.forEach((id: any) => {
          if (creativesStore.items[id].attributes) {
            creatives.push(creativesStore.items[id].attributes)
          }
        })
      }
      return creatives
    })

    const creativesToClone = ref(
      creatives.value
        .filter(
          (creative: ICreativeAttributes) =>
            creative.advert_state === 'approved'
        )
        .map((entry: ICreativeAttributes) => entry.id)
    )
    const dialogFormVisible = ref(false)
    const { router } = useRouter()

    const openModal = () => {
      dialogFormVisible.value = true
    }

    const closeModal = () => {
      dialogFormVisible.value = false
    }

    const cloneWithCreatives = (id: number | string) => {
      creativesStore.SetClonedItems(
        creativesToClone.value.map((item: string | number) => item)
      )
      router.push(`/campaigns/clone/${id}`)
    }

    const modalTitle = computed(() => {
      return `Clone campaign ${props.campaign.name} with creatives`
    })

    const viewReport = (id: string) => {
      router.push({ path: '/reports/full', query: { campaignId: id } })
    }

    return {
      onDelete,
      onArchive,
      viewReport,
      dialogFormVisible,
      closeModal,
      openModal,
      modalTitle,
      creatives,
      cloneWithCreatives,
      creativesToClone
    }
  }
})
</script>

<style lang="scss" scoped>
.mr-1 {
  margin-right: 1em;
}

.mobile .campaign-actions {
  display: flex;
  margin-top: 0.5em;
  justify-content: flex-end;
}

.el-dropdown-menu__item a {
  display: inline-block;
  height: 100%;
  width: 100%;
}

.small-action-btn {
  margin-right: 3px;
}

.clone-champaign-dropdown {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px;
}

.modal-content {
  display: flex;
  flex-direction: column;
  gap: 2px;

  .content-item {
    display: flex;
    gap: 10px;
  }

  .item-id {
    font-size: 12px;
    color: grey;
    font-weight: 500;
  }
}

.mass-action {
  display: flex;
  justify-content: center;
}

.modal-text {
  margin-bottom: 10px;
}

:deep(.el-dropdown-menu__item) {
  padding: 0;
  line-height: normal;
  width: fit-content;
}

:deep(.el-dropdown-menu__item:hover) {
  background: none;
}

:deep(.el-dialog__wrapper) {
  background: rgba(0, 0, 0, 0.4);
}

:deep(.el-dialog__body) {
  padding: 0 20px;
}
</style>
